import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMonsterImgPath } from '../utils/monster';
import MonsterImage from '../components/MonsterImage';
import { monstersById } from './SimMemory';

/** 학습 서버 상태 (백엔드 GET /api/sim/train — swSim engine/src/trainremote.ts) */
interface TrainState {
    running: boolean;
    job: { index: number; defense: string; state: 'running' | 'publishing' | 'done' | 'failed' | 'cancelled'; started: string; finished: string } | null;
    other: boolean;
    /** 모든 방덱 한꺼번에 학습이 돌면 — 지금 하는 방덱·끝난 수·전체 */
    all?: { defense: number[]; index: number; total: number; done: number; recent?: Array<{ defense: number[]; win: number }>; avgSec?: number; current?: number };
    /** 학습 전체에서 몇 % */
    percent: number;
    /** 학습 속도 — 1초에 친 전투 수 */
    speed?: number;
    /** 지금 단계 이름 ('학습 준비 중' · '후보 전투 중 · 24판' · '1000판 확정 중' · '룬 피팅 중' …) */
    stage?: string;
    published?: number[];
    error?: string;
}

/** 방금 끝난 방덱 — 접었을 때 보일 수 (펼치면 서버가 주는 10개까지) */
const RECENT_FEW = 3;
/**
 * 내가 시작한 [공덱 학습] — { def: '몹-몹-몹', at: 시작 시각 }. [결과 보기]는 이 학습이 끝났을 때만 보인다
 * (사용자 2026-10-27 "결과 보기는 그냥 띄우는게 아니라 공덱을 학습했을 때 볼 수 있게"). 새로고침해도 남게 이 브라우저에 적어 둔다.
 */
const MINE_KEY = 'swpvpdb-simtrain-mine';
/**
 * 3초마다 오는 % 를 그 사이 고르게 — 오를 땐 지금 보이는 값에서 새 값까지 ms 동안 0.1초마다 일정하게, 내려가면(다음 방덱) 바로.
 * 받은 값을 그대로 그리면 3초마다 한 번에 쫙 올랐다 (사용자 2026-10-27 "한번에 갑자기 쫙 올라 1초마다 갱신되는게 아니고").
 */
function useTween(target: number, ms = 3000): number {
    const [shown, setShown] = useState(target);
    const shownRef = useRef(target);
    shownRef.current = shown;
    useEffect(() => {
        const a = shownRef.current;
        if (target <= a) { setShown(target); return; }
        const t0 = Date.now();
        const t = setInterval(() => {
            const f = Math.min(1, (Date.now() - t0) / ms);
            setShown(a + (target - a) * f);
            if (f >= 1) clearInterval(t);
        }, 100);
        return () => clearInterval(t);
    }, [target, ms]);
    return shown;
}
const readMine = (): { def: string; at: string } | null => { try { return JSON.parse(localStorage.getItem(MINE_KEY) || 'null'); } catch { return null; } };
const writeMine = (v: { def: string; at: string }) => { try { localStorage.setItem(MINE_KEY, JSON.stringify(v)); } catch { /* 못 적어도 이번 화면에선 state 로 */ } };
/** 초 → '58초' · '1분 12초' */
const secText = (s: number) => (s < 60 ? `${s}초` : `${Math.floor(s / 60)}분${s % 60 ? ` ${s % 60}초` : ''}`);

/**
 * **[공덱 학습]** — 지금 방덱으로 학습 서버에서 공덱을 학습한다(전수 탐색 → 룬 맞추기 → 학습 기록). 끝나면 [학습기록]에 올라간다
 * (사용자 2026-10-26 "사이트 시뮬레이션 탭에서 학습 시킬 수 있도록 하자. 학습 진행중인게 있으면 학습 중 이 떠서 안되게 하고. 없으면 해당 방덱으로 학습되게").
 * 학습 서버 상태를 30초마다 묻는다. 무엇이든 학습이 돌고 있으면 단추를 막고 '학습 중'.
 */
export default function SimTrain({ defense, slots, set, monsterMapping, isMobile, onResult }: {
    /** 방덱 칸의 몹 번호 (빈 칸은 null) — 첫 칸이 리더 */
    defense: Array<number | null | undefined>;
    /**
     * 방덱 칸의 능력치(SimPage toInput)·칸마다 룬을 넣었나 — 실전 룬 표본이 없는 몹은 서버가 이것으로 학습한다
     * (넣은 룬, 없으면 실전 중앙값 — 사용자 2026-10-29 "방덱 몹 17413 의 실전 방어 룬 표본이 없어 학습할 수 없습니다" → "그렇게 진행해")
     */
    slots?: unknown[];
    set?: boolean[];
    monsterMapping: any;
    isMobile: boolean;
    /** [결과 보기] — 끝난 학습의 방덱(몹 번호 셋)으로 학습 기록 1등 공덱을 칸에 넣는다 (SimPage) */
    onResult?: (defense: number[]) => void;
}) {
    const [st, setSt] = useState<TrainState | null>(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [mine, setMine] = useState(readMine);
    /** 방금 끝난 방덱 — 펼쳐서 10개까지 보나 */
    const [more, setMore] = useState(false);
    const byId = useMemo(() => monstersById(monsterMapping), [monsterMapping]);
    const label = (id: number) => {
        const m = byId.get(Number(id));
        return m ? getMonsterImgPath(monsterMapping, m).displayName || m.name : String(id);
    };
    const names = (ids: Array<number | string>) => ids.map((x) => label(Number(x))).join(' · ');

    const alive = useRef(true);
    const load = useCallback(async (fresh = false) => {
        try {
            const d = await fetch(`/api/sim/train${fresh ? '?fresh=1' : ''}`, { credentials: 'include' }).then((r) => r.json());
            if (!alive.current || !d?.success) return;
            // 방금 누른 학습이 학습 서버에 아직 안 보이면(누른 뒤 15초 안 · 마지막 학습이 그 전 것) 이 답은 버린다 —
            // 그대로 쓰면 진행 막대가 잠깐 사라지고 [공덱 학습]·[결과 보기]가 다시 보였다 (2026-10-27)
            const m = readMine();
            const startedAfter = (j: any) => !!j && j.defense === m?.def && Date.parse(j.started) >= Date.parse(m!.at) - 120000;
            if (m && !d.running && Date.now() - Date.parse(m.at) < 15000 && !startedAfter(d.job)) return;
            setSt(d);
            if (d.published?.length) setMsg(`학습이 끝나 [학습기록]에 올렸습니다 — ${names(d.published)}`);
        } catch {
            /* 다음 물음에서 */
        }
    }, [byId]); // eslint-disable-line react-hooks/exhaustive-deps
    // 학습이 돌면 3초마다(지금 방덱 % 를 실시간으로 — 사용자 2026-10-27), 아니면 30초마다 묻는다. 탭이 안 보이면 30초
    const runningRef = useRef(false);
    runningRef.current = !!st?.running;
    useEffect(() => {
        alive.current = true;
        let t: ReturnType<typeof setTimeout>;
        const tick = async () => {
            await load();
            if (!alive.current) return;
            t = setTimeout(tick, runningRef.current && document.visibilityState === 'visible' ? 3000 : 30000);
        };
        tick();
        return () => { alive.current = false; clearTimeout(t); };
    }, [load]);

    const start = async () => {
        setErr(''); setMsg('');
        const ids = defense.map((x) => Number(x) || 0);
        if (ids.some((x) => !x)) { setErr('방덱 세 칸을 모두 채워 주세요'); return; }
        // 확인 창 없이 바로 시작한다 (사용자 2026-10-27 "팝업창 만드는거 없애고 바로 학습시작")
        setBusy(true);
        try {
            const r = await fetch('/api/sim/train', {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ def: ids, ...(slots ? { slots, set } : {}) })
            });
            const d = await r.json();
            if (d?.success) {
                const m = { def: ids.join('-'), at: new Date().toISOString() };
                writeMine(m); setMine(m);
                // 다음 물음을 기다리지 않고 바로 '학습 중' 으로 — 진행 막대가 누르자마자 보이게 (사용자 2026-10-27 "저 시간을 줄일 수는 없어?")
                setSt((cur) => ({ ...(cur || { other: false, percent: 0 }), running: true, other: false, percent: 0, error: undefined,
                    job: { index: Number(d.index) || 0, defense: m.def, state: 'running', started: m.at, finished: '' } }));
            }
            if (d?.busy) setErr(d.error || '학습 중입니다');
            else if (!d?.success) setErr(d?.error || '학습을 시작하지 못했습니다');
            // 시작했다는 문구는 띄우지 않는다 — 진행 막대가 바로 보인다 (사용자 2026-10-27 "학습을 시작했습니다 빼")
        } catch {
            setErr('학습을 시작하지 못했습니다');
        }
        setBusy(false);
        load(true);
    };

    /** 학습 취소 — 사이트에서 시작한 학습, 또는 모든 방덱 학습 (사용자 2026-10-27 "학습 취소도 가능하게") */
    const cancel = async () => {
        const all = !!st?.other && !!st?.all;
        const ask = all
            ? `모든 방덱 학습을 멈출까요?\n\n끝난 ${st!.all!.done}개 방덱 결과는 남고, 지금 하던 방덱(${names(st!.all!.defense)})만 버려집니다.\n다시 돌리면 끝난 방덱은 건너뛰고 이어서 합니다.`
            : `학습을 취소할까요?\n\n${names(jobIds)}\n\n지금까지 한 것은 버려집니다.`;
        if (!window.confirm(ask)) return;
        setErr(''); setMsg('');
        setBusy(true);
        try {
            const d = await fetch('/api/sim/train/cancel', { method: 'POST', credentials: 'include' }).then((r) => r.json());
            if (!d?.success) setErr(d?.error || '취소하지 못했습니다');
            else setMsg(d.all ? `모든 방덱 학습을 멈췄습니다 — 끝난 ${st?.all?.done ?? 0}개는 남아 있습니다` : `학습을 취소했습니다 — ${names(jobIds)}`);
        } catch {
            setErr('취소하지 못했습니다');
        }
        setBusy(false);
        load(true);
    };

    const running = !!st?.running;
    const off = running || busy || !st || !!st.error;
    /** 마지막 [공덱 학습]이 끝났으면 그 방덱 — [결과 보기] */
    // 내가 시작한 그 방덱 학습이 끝났을 때만 (학습 서버 train-job 의 시작 시각이 내가 누른 때보다 늦어야 — 시계 차이로 2분 여유)
    const mineDone = !!st?.job && st.job.state === 'done' && !!mine && st.job.defense === mine.def
        && Date.parse(st.job.started) >= Date.parse(mine.at) - 120000;
    const doneIds = mineDone ? st!.job!.defense.split('-').map(Number).filter((x) => x > 0) : [];
    const jobIds = st?.job?.defense ? st.job.defense.split('-') : [];
    /** 진행 막대에 보일 방덱 — 우리 학습이면 그 방덱, 모든 방덱 학습이면 지금 하는 방덱 */
    const barIds: Array<number | string> = running ? (st?.other ? st?.all?.defense || [] : jobIds) : [];
    /** 진행 제목과 그 아래 작은 글자 (끝난 수) */
    const barTitle = !running ? '' : !st?.other ? '학습 중' : st?.all ? '전체 방덱 학습 중' : '다른 학습 중';
    /** 막대 아래 숫자 칸 [값, 이름, 마우스를 올리면] — 완료 · 방덱당 평균(최근 10개) · 전투/초(최근 20초) */
    const all = running && st?.other ? st?.all : undefined;
    const stats: Array<[string, string, string]> = [
        ...(all ? [[`${all.done} / ${all.total}`, '완료', '끝난 방덱 / 전체 방덱'] as [string, string, string]] : []),
        ...(all?.avgSec ? [[secText(all.avgSec), '방덱당 평균', '최근 끝난 방덱 10개의 평균'] as [string, string, string]] : []),
        ...(running && st?.speed ? [[st.speed.toLocaleString(), '전투/초', '학습 서버가 1초에 치는 전투 수 (최근 20초)'] as [string, string, string]] : [])
    ];
    const showPct = running && (!st?.other || !!st?.all);
    const status = !st ? '학습 서버 상태를 묻는 중…'
        : st.error ? '학습 서버에 닿지 못했습니다'
        : running ? ''   // 돌고 있으면 아래 진행 막대가 보인다
        : st.job?.state === 'failed' ? `지난 학습이 중간에 멈췄습니다 — ${names(jobIds)}`
        : '';

    /** 몹 번호 셋 → 그림 셋 */
    const mobs = (ids: Array<number | string>, size: number, gap = 4) => (
        <span style={{ display: 'inline-flex', gap, flexShrink: 0 }}>
            {ids.map((id, i) => {
                const m = byId.get(Number(id));
                const img = m ? getMonsterImgPath(monsterMapping, m) : null;
                return img?.imgPath ? (
                    <MonsterImage key={i} imgPath={img.imgPath} alt={img.displayName}
                        style={{ width: size, height: size, borderRadius: Math.round(size / 4), objectFit: 'cover' }} />
                ) : null;
            })}
        </span>
    );
    const recent = running && st?.other ? st?.all?.recent || [] : [];
    /** 화면에 보이는 % — 받은 값 사이를 고르게 (useTween) */
    const pctShown = useTween(st?.running ? st.percent : 0);
    const curShown = useTween(st?.running ? st?.all?.current ?? 0 : 0);
    /** 이름 · 막대 · % 한 줄 (모든 방덱 학습의 '전체'·'지금 방덱') */
    const gauge = (label: string, pct: number, fill: string, track: string, color: string, top: number) => {
        return (
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, marginTop: top }}>
            <span style={{ width: isMobile ? 50 : 56, flexShrink: 0, fontSize: isMobile ? 10.5 : 11, fontWeight: 800, color: '#9a9dbf' }}>{label}</span>
            <div style={{ flex: 1, height: isMobile ? 7 : 8, borderRadius: 999, background: track, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 999, width: `${Math.max(2, pct)}%`, background: fill }} />
            </div>
            <span style={{ width: 34, flexShrink: 0, textAlign: 'right', fontSize: isMobile ? 11 : 12, fontWeight: 900, color }}>{Math.floor(pct)}%</span>
        </div>
        );
    };
    const canCancel = running && (!st?.other || !!st?.all);

    return (
        <>
            {/* 학습이 돌고 있으면 [공덱 학습] 단추는 숨기고 진행만 보인다 (회색 '학습 중' 단추가 자리만 먹었다 — 2026-10-27 초안 10번) */}
            {!running && (
                <button type="button" onClick={start} disabled={off} title={status || '지금 방덱으로 학습 서버에서 공덱을 학습합니다'} style={{
                    border: `2px solid ${off ? '#bbb' : '#2e7d32'}`, borderRadius: 12,
                    padding: isMobile ? '7px 14px' : '11px 30px', fontSize: isMobile ? 14 : 16, fontWeight: 900,
                    color: off ? '#999' : '#2e7d32', background: off ? '#f2f2f2' : '#fff', cursor: off ? 'not-allowed' : 'pointer'
                }}>{busy ? '시작하는 중…' : '공덱 학습'}</button>
            )}
            {/*
              [결과 보기] — [공덱 학습] 오른쪽(사용자 2026-10-27). 끝난 학습의 1등 공덱을 바로 칸에 넣는다 (사용자 2026-10-27 "학습 끝나면 결과 보기 버튼 만들어서 누르면 해당 공덱이 바로 적용").
              이 브라우저에서 내가 시작한 학습이 끝났을 때만 보인다(다음 학습을 시작하기 전까지) — 남이 시작했거나 옛 학습이면 안 보인다.
            */}
            {!running && doneIds.length === 3 && onResult && (
                <button type="button" onClick={() => onResult(doneIds)} title={`학습한 방덱: ${names(doneIds)}`} style={{
                    marginLeft: isMobile ? 8 : 10, border: 'none', borderRadius: 12,
                    padding: isMobile ? '7px 14px' : '11px 26px', fontSize: isMobile ? 14 : 16, fontWeight: 900, color: '#fff', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #2ea05a 0%, #1f7a44 100%)', boxShadow: '0 4px 14px rgba(46,160,90,0.35)'
                }}>결과 보기</button>
            )}
            {/*
              진행 — 몹 그림 셋 · 학습 중 · % · 막대 · [취소], 그 아래 '방금 끝난 방덱' 셋 (사용자 2026-10-27 학습 판 초안 10번 · 1등 승률은 빼기로).
              방금 끝난 방덱은 모든 방덱 학습일 때만(학습 서버 run-all.log). [취소]는 사이트 학습과 모든 방덱 학습에서.
            */}
            {running && (
                <div style={{ flexBasis: '100%', width: '100%', maxWidth: isMobile ? 380 : 560, margin: '0 auto' }}>
                    {/* 깜빡이는 초록 점 — '지금 돌고 있다' 표시 */}
                    <style>{'@keyframes simTrainPulse{0%{box-shadow:0 0 0 0 rgba(46,160,90,.5)}70%{box-shadow:0 0 0 6px rgba(46,160,90,0)}100%{box-shadow:0 0 0 0 rgba(46,160,90,0)}}'
                        + '@keyframes simTrainStage{from{opacity:0;transform:translateY(2px)}to{opacity:1;transform:none}}.sim-train-stage{animation:simTrainStage .25s ease-out}'}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
                        {/* 지금 학습 중인 방덱 — 연보라 틀에 담아 아래 '끝난 방덱'과 가른다 */}
                        {barIds.length > 0 && (
                            <span style={{
                                display: 'inline-flex', padding: isMobile ? 4 : 5, borderRadius: 14, flexShrink: 0,
                                background: 'linear-gradient(135deg, rgba(102,126,234,0.12), rgba(118,75,162,0.12))',
                                boxShadow: 'inset 0 0 0 1.5px rgba(102,126,234,0.35)'
                            }}>{mobs(barIds, isMobile ? 26 : 32, isMobile ? 3 : 4)}</span>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ea05a', flexShrink: 0, animation: 'simTrainPulse 1.8s ease-out infinite' }} />
                                <span title={names(barIds)} style={{
                                    fontSize: isMobile ? 12 : 14, fontWeight: 900, color: '#3d3f66',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0
                                }}>{barTitle}</span>
                                {/* 지금 단계 — 학습 준비 중 · 후보 전투 중 · 1000판 확정 중 · 룬 피팅 중 … (사용자 2026-10-28 "상태에 단계를 표시해보자") */}
                                {st?.stage && (
                                    <span key={st.stage} className="sim-train-stage" style={{
                                        flexShrink: 0, whiteSpace: 'nowrap', borderRadius: 999, padding: isMobile ? '2px 8px' : '3px 10px',
                                        fontSize: isMobile ? 10.5 : 11.5, fontWeight: 800,
                                        ...(st.stage.startsWith('룬 피팅')
                                            ? { background: 'rgba(46, 160, 90, 0.12)', color: '#2e8b57' }
                                            : { background: 'rgba(102, 126, 234, 0.12)', color: '#5a5fd6' })
                                    }}>{st.stage}</span>
                                )}
                                {/* 모든 방덱 학습이면 % 는 아래 '전체'·'지금 방덱' 줄에만 (제목 옆 % 는 빼기로, 사용자 2026-10-27) */}
                                {showPct && !all && <span style={{ marginLeft: 'auto', fontSize: isMobile ? 14 : 17, fontWeight: 900, color: '#667eea', letterSpacing: -0.3 }}>{Math.floor(pctShown)}%</span>}
                            </div>
                            {/*
                              모든 방덱 학습이면 막대 두 줄 — 위 '전체'(보라) · 아래 '지금 방덱'(초록, 지금 학습 중인 방덱 하나의 진행)
                              (사용자 2026-10-27 "현재 진행중인 방덱의 게이지도 표시해주자. 초록색으로" 초안 4번). 그 밖에는 보라 막대 하나.
                            */}
                            {all ? (
                                <>
                                    {gauge('전체', pctShown, 'linear-gradient(90deg, #667eea, #764ba2)', 'rgba(102, 126, 234, 0.14)', '#667eea', 6)}
                                    {gauge('지금 방덱', curShown, 'linear-gradient(90deg, #3cc47c, #2ea05a)', 'rgba(46, 160, 90, 0.15)', '#2ea05a', 4)}
                                </>
                            ) : (
                                <div style={{ height: isMobile ? 7 : 9, borderRadius: 999, background: 'rgba(102, 126, 234, 0.14)', overflow: 'hidden', marginTop: 6 }}>
                                    <div style={{
                                        height: '100%', borderRadius: 999, width: `${showPct ? Math.max(2, pctShown) : 100}%`,
                                        background: 'linear-gradient(90deg, #667eea, #764ba2)', opacity: showPct ? 1 : 0.35
                                    }} />
                                </div>
                            )}
                            {/*
                              막대 아래 숫자 칸 — 완료 · 방덱당 평균 · 전투/초 (사용자 2026-10-27 초안 3번). 사이트 학습(방덱 하나)은 전투/초만.
                            */}
                            {stats.length > 0 && (
                                <div style={{ display: 'flex', gap: isMobile ? 5 : 6, marginTop: isMobile ? 7 : 8 }}>
                                    {stats.map(([value, label, title]) => (
                                        <div key={label} title={title} style={{
                                            flex: 1, minWidth: 0, textAlign: 'center', background: '#fff', borderRadius: 10,
                                            border: '1px solid rgba(102, 126, 234, 0.14)', padding: isMobile ? '5px 4px' : '6px 8px'
                                        }}>
                                            <div style={{ fontSize: isMobile ? 12.5 : 14, fontWeight: 900, color: '#5a5fd6', whiteSpace: 'nowrap' }}>{value}</div>
                                            <div style={{ fontSize: isMobile ? 9.5 : 10, fontWeight: 700, color: '#9a9dbf' }}>{label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/*
                          [취소] — 사이트 차림(둥근 알약 · 옅은 바탕)에 맞춘 조용한 빨강. 테두리 선만 있던 네모 단추가 혼자 튀었다 (2026-10-27).
                          누르기 전엔 옅게, 마우스를 올리면 진하게.
                        */}
                        {canCancel && (
                            <button type="button" onClick={cancel} disabled={busy} title="학습 취소"
                                onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = '#e5484d'; e.currentTarget.style.color = '#fff'; } }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229, 72, 77, 0.10)'; e.currentTarget.style.color = '#e5484d'; }}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, alignSelf: 'center',
                                    border: 'none', borderRadius: 999, background: 'rgba(229, 72, 77, 0.10)', color: '#e5484d',
                                    fontSize: isMobile ? 11 : 12, fontWeight: 800, padding: isMobile ? '6px 10px' : '7px 14px',
                                    cursor: busy ? 'not-allowed' : 'pointer', transition: 'background 0.15s ease, color 0.15s ease'
                                }}>
                                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                                {busy ? '취소 중…' : '취소'}
                            </button>
                        )}
                    </div>
                    {recent.length > 0 && (
                        <div style={{ borderTop: '1px solid rgba(102, 126, 234, 0.12)', marginTop: isMobile ? 12 : 14, paddingTop: isMobile ? 10 : 12 }}>
                            {/* 처음엔 3개, [더 보기]로 10개까지 (사용자 2026-10-27 "10개까지도 볼 수 있게") */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                                    <circle cx="7" cy="7" r="7" fill="rgba(46,160,90,0.15)" />
                                    <path d="M4 7.2l2 2 4-4.4" fill="none" stroke="#2ea05a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span style={{ fontSize: isMobile ? 11 : 12, fontWeight: 800, color: '#6b6e94' }}>방금 끝난 방덱</span>
                                {recent.length > RECENT_FEW && (
                                    <button type="button" onClick={() => setMore((v) => !v)} style={{
                                        marginLeft: 'auto', border: 'none', borderRadius: 999, background: 'rgba(102, 126, 234, 0.10)', cursor: 'pointer',
                                        fontSize: isMobile ? 10.5 : 11, fontWeight: 800, color: '#667eea', padding: '3px 10px',
                                        display: 'inline-flex', alignItems: 'center', gap: 4
                                    }}>
                                        {more ? '접기' : `${recent.length}개 모두 보기`}
                                        <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true" style={{ transform: more ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
                                            <path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                            {/* 방덱마다 작은 칸 — 칸 너비를 맞춰 줄이 바뀌어도 가지런히. 가장 최근 것(맨 앞)은 초록 테두리 */}
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 74 : 86}px, 1fr))`, gap: isMobile ? 6 : 8 }}>
                                {(more ? recent : recent.slice(0, RECENT_FEW)).map((r, i) => (
                                    <span key={i} title={names(r.defense)} style={{
                                        display: 'flex', justifyContent: 'center', padding: isMobile ? '5px 4px' : '6px 5px', borderRadius: 10,
                                        background: '#f5f6ff', border: `1px solid ${i === 0 ? 'rgba(46,160,90,0.45)' : 'rgba(102,126,234,0.14)'}`
                                    }}>
                                        {mobs(r.defense, isMobile ? 20 : 22, 2)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {(status || (msg && !running) || err) && (
                <div style={{ flexBasis: '100%', textAlign: 'center', fontSize: isMobile ? 11 : 12, fontWeight: 700, lineHeight: 1.5, marginTop: 4 }}>
                    {status && <div style={{ color: '#888' }}>{status}</div>}
                    {msg && !running && <div style={{ color: '#555' }}>{msg}</div>}
                    {err && <div style={{ color: '#d32f2f' }}>{err}</div>}
                </div>
            )}
        </>
    );
}
